import React, { Component } from 'react';
import { func, shape, string } from 'prop-types';
import I18n from 'i18n-js';
import { Form, Row, Col, Icon, Tooltip } from 'grab-ui';
import moment from 'moment';
import classnames from 'classnames';
import { isEmpty } from 'common/utils/data-utils';
import InputField from 'components/form-field/input-field/input-field';
import SelectField from 'components/form-field/select-field/select-field';
import DatePickerField from 'components/form-field/date-picker-field/date-picker-field';
import { FILTER, ORDER_STATE } from 'constants/history-constants';
import { TOOLTIP } from 'constants/app-constants';

import css from './filter-form.less';

export const ORDER_STATUS = {
  ALL: 'ALL_STATUS',
  SCHEDULED: 'SCHEDULED',
  PICKING_UP: 'PICKING_UP',
  DROPPING_OFF: 'DROPPING_OFF',
  ACTIVE_DELIVERY: 'ACTIVE_DELIVERY',
  CANCELLED: 'CANCELLED',
  COMPLETED: 'COMPLETED',
  REALLOCATED: 'REALLOCATED',
  NO_DRIVER: 'NO_DRIVER',
  PENDING_PICK_UP: 'PENDING_PICK_UP',
  PENDING_DROP_OFF: 'PENDING_DROP_OFF',
  SENDER_CACNCELLED: 'SENDER_CANCELLED',
  DRIVER_CANCELLED: 'DRIVER_CANCELLED',
  OPERATOR_CANCELLED: 'OPERATOR_CANCELLED',
};

const FIELD_NAME = {
  KEYWORD: 'keyword',
  ORDER_STATE: 'orderState',
  FROM_DATE: 'fromDate',
  TO_DATE: 'toDate',
};

const DATE_FORMAT = 'MMDDYYYY';

const MAX_DAYS_QUERYABLE = 10;

export class FilterForm extends Component {
  static propTypes = {
    onChangeFilterData: func.isRequired,
    handleSelectFilter: func.isRequired,
    form: shape({}).isRequired,
    isFilterChecked: string,
  };

  static defaultProps = {
    isFilterChecked: undefined,
  };

  constructor(props) {
    super(props);
    this.state = {
      toDateInitial: moment(),
      toDateDefaultPicker: null,
    };
    this.changeKeywordTimeout = null;
    this.orderStatusOptions = [
      {
        value: ORDER_STATUS.ALL,
        child: (
          <h5 className={css.orderStatusOption}>
            {I18n.t(ORDER_STATE.ALL_STATUS)}
          </h5>
        ),
      },
      {
        value: ORDER_STATUS.ACTIVE_DELIVERY,
        child: (
          <Tooltip
            placement="right"
            title={I18n.t(TOOLTIP.ACTIVE_DELIVERY_ITEM)}
          >
            <h5 className={css.orderStatusOption}>
              {I18n.t(ORDER_STATE.ACTIVE_DELIVERY)}
            </h5>
          </Tooltip>
        ),
      },
      {
        value: ORDER_STATUS.SCHEDULED,
        child: (
          <h5 className={css.orderStatusOption}>
            {I18n.t(ORDER_STATE.SCHEDULED)}
          </h5>
        ),
      },
      {
        value: ORDER_STATUS.PICKING_UP,
        child: (
          <h5 className={css.orderStatusOption}>
            {I18n.t(ORDER_STATE.PICKING_UP)}
          </h5>
        ),
      },
      {
        value: ORDER_STATUS.DROPPING_OFF,
        child: (
          <h5 className={css.orderStatusOption}>
            {I18n.t(ORDER_STATE.DROPPING_OFF)}
          </h5>
        ),
      },
      {
        value: ORDER_STATUS.COMPLETED,
        child: (
          <h5 className={css.orderStatusOption}>
            {I18n.t(ORDER_STATE.COMPLETED)}
          </h5>
        ),
      },
      {
        value: ORDER_STATUS.CANCELLED,
        child: (
          <h5 className={css.orderStatusOption}>
            {I18n.t(ORDER_STATE.CANCELLED)}
          </h5>
        ),
      },
    ];
  }

  onChangeKeyword = () => {
    const { onChangeFilterData, form: { getFieldsValue } } = this.props;
    if (this.changeKeywordTimeout) {
      clearTimeout(this.changeKeywordTimeout);
    }

    this.changeKeywordTimeout = setTimeout(() => {
      const fieldValues = getFieldsValue();
      fieldValues.fromDate = moment(fieldValues.fromDate).format('YYYY-MM-DD');
      fieldValues.toDate = moment(fieldValues.toDate).format('YYYY-MM-DD');
      onChangeFilterData(fieldValues);
    }, 500);
  };

  onDisabledFromDate = fromDate => {
    const { form: { getFieldValue } } = this.props;
    const toDate = getFieldValue(FIELD_NAME.TO_DATE);

    if (!fromDate || !toDate) {
      return false;
    }
    return fromDate.isAfter(moment());
  };

  onDisabledToDate = toDate => {
    const { form: { getFieldValue } } = this.props;
    const fromDate = getFieldValue(FIELD_NAME.FROM_DATE);

    if (!toDate || !fromDate) {
      return false;
    }
    const beforeStartDate = toDate.valueOf() < fromDate.valueOf();
    const afterMaxQueryLength =
      toDate.diff(fromDate, 'days') > MAX_DAYS_QUERYABLE;
    return beforeStartDate || afterMaxQueryLength;
  };

  onChangeFilterData = () => {
    const {
      onChangeFilterData,
      form: { getFieldsValue, setFieldsValue },
      handleSelectFilter,
    } = this.props;
    setTimeout(() => {
      const fieldValues = getFieldsValue();
      const fromDate = fieldValues.fromDate ? moment(fieldValues.fromDate) : '';
      const toDate = fieldValues.toDate ? moment(fieldValues.toDate) : '';
      if (!fromDate || !toDate) {
        return;
      }

      const allowed = this.checkValidDateRange(
        fromDate,
        toDate,
        MAX_DAYS_QUERYABLE,
      );
      if (!allowed) {
        setFieldsValue({ toDate: null }, () => {
          this.setState({
            toDateInitial: null,
            toDateDefaultPicker: fromDate,
          });
        });
        return;
      }

      fieldValues.fromDate = fromDate.format('YYYY-MM-DD');
      fieldValues.toDate = toDate.format('YYYY-MM-DD');
      fieldValues.keyword = fieldValues.keyword || '';

      handleSelectFilter(fieldValues.orderState);
      onChangeFilterData(fieldValues);
    });
  };

  checkValidDateRange = (fromDate, toDate, maxDays) => {
    const withinMaxQueryRange = toDate.diff(fromDate, 'days') <= maxDays;
    return withinMaxQueryRange && fromDate.isSameOrBefore(toDate);
  };

  dateRender = currentDate => {
    const { form: { getFieldValue } } = this.props;
    const fromDate = getFieldValue(FIELD_NAME.FROM_DATE);
    const toDate = getFieldValue(FIELD_NAME.TO_DATE);
    if (isEmpty(fromDate) || isEmpty(toDate)) {
      return (
        <div className={classnames('ant-calendar-date', css.dateHover)}>
          {currentDate.date()}
        </div>
      );
    }
    if (
      currentDate.format(DATE_FORMAT) === fromDate.format(DATE_FORMAT) ||
      currentDate.format(DATE_FORMAT) === toDate.format(DATE_FORMAT) ||
      (currentDate.valueOf() >= fromDate.valueOf() &&
        currentDate.valueOf() <= toDate.valueOf())
    ) {
      return (
        <div
          className={classnames(
            'ant-calendar-date',
            css.dateHover,
            css.hightlightDate,
          )}
        >
          {currentDate.date()}
        </div>
      );
    }
    return (
      <div className={classnames('ant-calendar-date', css.dateHover)}>
        {currentDate.date()}
      </div>
    );
  };
  render() {
    const { form, isFilterChecked } = this.props;
    const { toDateInitial, toDateDefaultPicker } = this.state;

    return (
      <Form>
        <Row gutter={26}>
          <Col span={8}>
            <InputField
              id="keyword"
              label={I18n.t('history.fieldLabel.searchDelivery')}
              placeholder={I18n.t('history.placeHolder.searchDelivery')}
              onChange={this.onChangeKeyword}
              isSearchField
              form={form}
            />
          </Col>
          <Col span={7}>
            <SelectField
              id="orderState"
              label={I18n.t(FILTER.BY_STATUS)}
              options={this.orderStatusOptions}
              initialValue={isFilterChecked}
              onSelect={this.onChangeFilterData}
              form={form}
              dropdownClassName={css.orderStatusDropdown}
              showSelectedMarker
            />
          </Col>
          <Col span={8} className={css.dateFilter}>
            <DatePickerField
              id="fromDate"
              label={I18n.t(FILTER.BY_DATE)}
              placeholder="Select start date"
              format="DD/MM/YYYY"
              onChange={this.onChangeFilterData}
              initialValue={moment().subtract(7, 'days')}
              disabledDate={this.onDisabledFromDate}
              dateRender={this.dateRender}
              form={form}
            />
            <Icon type="arrow-right" className={css.dateDevideIcon} />
            <DatePickerField
              id="toDate"
              label=" "
              placeholder="Select end date"
              format="DD/MM/YYYY"
              onChange={this.onChangeFilterData}
              initialValue={toDateInitial}
              disabledDate={this.onDisabledToDate}
              dateRender={this.dateRender}
              form={form}
              defaultPickerValue={toDateDefaultPicker}
            />
          </Col>
        </Row>
      </Form>
    );
  }
}

export default Form.create()(FilterForm);
